import PageTitle from '../headers/PageTitle';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIncident } from './hooks/incidentHooks';
import { OperationalPeriodModal } from '../IncidentConfig/OperationalPeriodModal';
import IncidentAdminToolbar from '../incidentAdminToolbar/IncidentAdminToolbar';
import StylishNewSelect from '../DesignSystems/New/StylishNewSelect';
import CloneDefaults from '../CloneDefaults/CloneDefaults';
import {
  selectGroup,
  selectGroups,
  useAppSelector,
} from '../../slices/commonSelectors';
import useGroupAdminForCurrentIncident from '../../hooks/useGroupAdminForCurrentIncident';
import { startLoading } from '../../reducers/loading/loading.action';
import { updateIncidentGroup } from '../../actions/diceActions';
import IncidentTeamsFeatures from 'components/TemplateWizard/IncidentTeamsFeatures';
import MobileWarningDialog from 'components/DesignSystems/MobileWarning/MobileWarningDialog';
import { AdvanceOperationalPeriodModal } from './AdvanceOperationalPeriodModal';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  INCIDENT_TOUR_PARAM,
  wantsIncidentTour,
} from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourSteps';
import { UpgradeHoverLock } from 'components/Notifications/LockedUpgradeOption';
import {
  useHubOnlyFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import { registerFocIncidentRosterTourApi } from 'components/MapPage/focIncidentRosterTour/focIncidentRosterTourBridge';
import {
  FOC_INCIDENT_ROSTER_TOUR_PARAM,
  focIncidentRosterHref,
} from 'components/MapPage/focIncidentRosterTour/focIncidentRosterTourSteps';

const IAPSettings = () => {
  const incident = useIncident();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const hubOnly = useHubOnlyFlag();
  const uscgFoc = useUscgFocRequirementsFlag();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPeriodAdvancementModal, setShowPeriodAdvancementModal] =
    useState(false);
  const [showModal, setShowModal] = useState(false);

  // Last leg of the Incident Lifecycle tour: spotlight Archive, open nothing.
  const wantsTour = wantsIncidentTour(
    searchParams.get(INCIDENT_TOUR_PARAM),
    'archive'
  );
  const tourStartedRef = useRef(false);
  const focIncidentRosterStartedRef = useRef(false);
  const wantsFocIncidentRosterTour =
    uscgFoc && searchParams.get(FOC_INCIDENT_ROSTER_TOUR_PARAM) === '1';

  useEffect(() => {
    if (!wantsTour || tourStartedRef.current || !incident.data) return;

    const timer = window.setTimeout(() => {
      if (tourStartedRef.current) return;
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(INCIDENT_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import(
        'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTour'
      )
        .then(({ startIncidentLifecycleTour }) => {
          startIncidentLifecycleTour('archive');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsTour, incident.data, setSearchParams]);

  useEffect(() => {
    const incidentId = incident.data?.id;
    if (!incidentId) return;
    return registerFocIncidentRosterTourApi({
      getLocation: () => ({
        pathname: window.location.pathname,
        search: window.location.search,
      }),
      navigateToRosterSurface: () => {
        navigateRef.current(focIncidentRosterHref(String(incidentId)));
      },
    });
  }, [incident.data?.id]);

  useEffect(() => {
    if (!wantsFocIncidentRosterTour || focIncidentRosterStartedRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (focIncidentRosterStartedRef.current) return;
      focIncidentRosterStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_INCIDENT_ROSTER_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import(
        'components/MapPage/focIncidentRosterTour/focIncidentRosterTour'
      )
        .then(
          ({
            consumeFocIncidentRosterTourResume,
            startFocIncidentRosterTour,
          }) => {
            const resume = consumeFocIncidentRosterTourResume();
            startFocIncidentRosterTour({
              startId: resume ?? 'usir025',
            });
          }
        )
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocIncidentRosterTour, setSearchParams]);

  useEffect(() => {
    return () => {
      focIncidentRosterStartedRef.current = false;
    };
  }, []);

  const rbacPermissions =
    useAppSelector((state: any) => {
      return state.app.validateRBACPermissionForActionResult;
    }) || [];
  return (
    <MobileWarningDialog>
      <div className="maincontent">
        <div className="row">
          <div className="col-md-12 mb-5">
            <PageTitle
              title={'Incident Settings'}
              leftAlignedChildren={
                <Link to="../dashboard">
                  <StylishNewButton
                    className="button button--primary"
                    style={{ marginTop: '-1.25rem' }}
                  >
                    <SharedIcon iconName="person" classes="me-2" />
                    Back to Incident Workspace
                  </StylishNewButton>
                </Link>
              }
            >
              <UpgradeHoverLock locked={hubOnly}>
                <StylishNewButton
                  className="button--primary w-md-auto w-100"
                  disabled={hubOnly || !rbacPermissions['incident:create']}
                  onClick={() => navigate('/create/incident')}
                >
                  New Incident
                </StylishNewButton>
              </UpgradeHoverLock>
              {incident.isSuccess && (
                <div>
                  <StylishNewButton
                    className="button--secondary w-md-auto ms-md-3 mt-md-0 ms-0 me-3 mt-2 w-100"
                    disabled={!rbacPermissions['incident:create']}
                    data-tour="incident-op-period"
                    onClick={() => setShowModal(true)}
                  >
                    Operational Period
                  </StylishNewButton>
                  <StylishNewButton
                    className="button--secondary"
                    onClick={() => setShowPeriodAdvancementModal(true)}
                  >
                    Advance Operational Period
                  </StylishNewButton>
                </div>
              )}
              <IncidentTeamsFeatures />
            </PageTitle>
            <div className="w-75">
              <IncidentAdminToolbar />
              <SponsorOrganization />
              <CloneDefaults />
            </div>
          </div>
        </div>
        <OperationalPeriodModal
          showModal={showModal}
          setShowModal={setShowModal}
          incident={incident.data}
        />
      </div>
      {showPeriodAdvancementModal ? (
        <AdvanceOperationalPeriodModal
          showModal={showPeriodAdvancementModal}
          setShowModal={setShowPeriodAdvancementModal}
          data={incident.data}
        />
      ) : null}
    </MobileWarningDialog>
  );
};
export default IAPSettings;

const SponsorOrganization = () => {
  const dispatch = useDispatch();
  const [selectedGroup, setSelectedGroup] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [saveRequested, setSaveRequested] = useState(false);

  const userIsGroupAdmin = useGroupAdminForCurrentIncident();

  const currentGroup = useAppSelector(selectGroup);

  useEffect(() => {
    setSelectedGroup({
      label: currentGroup?.group_name || '',
      value: currentGroup?.group_guid || '',
    });
  }, [currentGroup]);

  const currentIncident = useAppSelector((state) => {
    return state.app.currentIncident;
  });

  const isSaveLoaded = useAppSelector((state) => {
    return state.app.isUpdateIncidentGroupLoaded;
  });

  const groups = useAppSelector((state) => {
    const rawGroups = selectGroups(state);
    return rawGroups
      .map((group) => ({
        groupGuid: group.group_guid,
        nameOfGroup: group.group_name,
        isCurrentUserPending: (!!(group.role === 'Pending')).toString(),
      }))
      .filter(({ isCurrentUserPending }) => isCurrentUserPending !== 'true')
      .map((group) => ({ label: group.nameOfGroup, value: group.groupGuid }));
  });

  useEffect(() => {
    if (isSaveLoaded) {
      setSaveRequested(false);
    }
  }, [isSaveLoaded]);

  const handleDropdownChange = (data: { label: string; value: string }) => {
    setSelectedGroup(data);
    setSaveRequested(true);
  };

  const handleSaveClick = () => {
    if (!selectedGroup) {
      return;
    }
    setSaveRequested(true);
    dispatch(startLoading());
    if (currentIncident) {
      dispatch(
        updateIncidentGroup({
          incidentGuid: currentIncident.id,
          groupGuid: selectedGroup.value,
        })
      );
    }
  };

  return (
    <div className="p-md-4 mt-4 rounded bg-gray-800 p-3">
      <h6 className="text-uppercase mt-0">Sponsor Organization</h6>
      <hr className="dashed my-3" />
      <span className="d-block txt--sml mb-3">
        ** You must have the proper permissions for the incident and sponsor
        organization to make changes
      </span>
      {groups && groups.length > 0 && (
        <>
          <StylishNewSelect
            options={groups}
            value={selectedGroup}
            placeholder="Select a Type"
            onChange={(e) => handleDropdownChange(e)}
            isClearable={false}
            isSearchable={false}
            isMulti={false}
            isDisabled={
              !(currentIncident?.role === 'Admin' || userIsGroupAdmin)
            }
          />
        </>
      )}
      <div className="button-group justify-content-end mt-4 flex-wrap">
        <StylishNewButton
          disabled={
            !saveRequested ||
            !(currentIncident?.role === 'Admin' || userIsGroupAdmin)
          }
          className="button--secondary"
          onClick={() => handleSaveClick()}
        >
          Save
        </StylishNewButton>
      </div>
    </div>
  );
};
